/**
 * Shared helper utilities for Zod schema preprocessing.
 *
 * @module udp-schema/helpers
 */

import { z } from 'zod';

/**
 * Converts empty / whitespace-only strings to `undefined`
 * so Zod `.optional()` treats them as "not provided".
 */
export const emptyStringToUndefined = (value: unknown): unknown => {
  if (typeof value === 'string' && value.trim() === '') {
    return undefined;
  }
  return value;
};

/**
 * Optional trimmed string with 100-char max.
 * Empty strings are coerced to `undefined`.
 */
export const optionalTrimmedString = z.preprocess(
  emptyStringToUndefined,
  z.string().trim().max(100).optional(),
);

/**
 * Optional 4-digit year (1900–2099).
 * Empty strings and undefined are treated as "not provided".
 * Accepts strings like "2020". Rejects non-digits and out-of-range values.
 *
 * Uses superRefine (not preprocess) to keep input type as `string | undefined`
 * for full react-hook-form compatibility.
 */
export const optionalYearSchema = z
  .string()
  .optional()
  .superRefine((val, ctx) => {
    if (!val || val.trim() === '') return; // empty / undefined → skip
    if (!/^\d{4}$/.test(val)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Enter a valid 4-digit year (e.g. 2020)',
      });
      return;
    }
    const n = Number(val);
    if (n < 1900 || n > 2099) {
      ctx.addIssue({
        code: 'custom',
        message: 'Year must be between 1900 and 2099',
      });
    }
  });

/**
 * Optional phone number — digits only, 7–15 characters.
 * Accepts an optional leading + for country code (e.g. +919876543210).
 * Empty strings and undefined are treated as "not provided".
 *
 * Uses superRefine (not preprocess) to keep input type as `string | undefined`
 * for full react-hook-form compatibility.
 */
export const optionalPhoneSchema = z
  .string()
  .optional()
  .superRefine((val, ctx) => {
    if (!val || val.trim() === '') return; // empty / undefined → skip
    if (!/^\+?[0-9]{7,15}$/.test(val)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Enter a valid phone number (digits only, 7–15 characters)',
      });
    }
  });
